import type { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { BidFormField } from '@tempmee/awards';
import {
  Checkbox,
  Chip,
  Column,
  FormField,
  InputTextAreaField,
  Radio,
  RadioGroup,
  Row,
  TextField,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BidFieldError, renderBidTermsLabel } from './bidForm';

interface IBidFormFieldRendererProps {
  field: BidFormField;
}

const TEXT_AREA_ROWS = 3;
// Per Figma (node 4732-3826): the 4 multiline application fields render at a
// fixed 144px height regardless of content.
const TEXT_AREA_HEIGHT_PX = 144;

const MAILING_ADDRESS_FIELD_NAME = 'mailingAddress';
export const MAILING_ADDRESS_HELPER_TEXT =
  "Best in Dental materials will be mailed here if you're selected.";

const FieldWrapper = ({
  name,
  children,
  errorMessage,
}: {
  name: string;
  children: ReactNode;
  errorMessage?: string;
}) => (
  <Column gap={1} data-cy={`bid-field-${name}`}>
    {children}
    <BidFieldError name={name} message={errorMessage} />
  </Column>
);

/**
 * Maps a `BidFormField` descriptor (from `@tempmee/awards`) to its DS input.
 * Registers with the ambient react-hook-form context (`useFormContext`) so
 * it can be dropped into any form built from a `BidFormDescriptor` —
 * reused by the EOD feedback form in GOTU-5643.
 */
export const BidFormFieldRenderer = ({ field }: IBidFormFieldRendererProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors[field.name]?.message as string | undefined;

  switch (field.type) {
    case 'text':
      // Per Figma, the question text is the textarea's placeholder — not a
      // separate label above it. `aria-label`/`aria-required` keep the field
      // accessible to screen readers even though no visible label renders.
      return (
        <FieldWrapper name={field.name} errorMessage={errorMessage}>
          <InputTextAreaField
            id={field.name}
            rows={TEXT_AREA_ROWS}
            height={TEXT_AREA_HEIGHT_PX}
            placeholder={field.label}
            aria-label={field.label}
            aria-required={field.required}
            isError={Boolean(errorMessage)}
            {...register(field.name)}
          />
        </FieldWrapper>
      );

    case 'address':
      return (
        <FieldWrapper name={field.name} errorMessage={errorMessage}>
          <FormField label={field.label} htmlFor={field.name} required={field.required}>
            <TextField
              id={field.name}
              error={Boolean(errorMessage)}
              helperText={
                field.name === MAILING_ADDRESS_FIELD_NAME ? MAILING_ADDRESS_HELPER_TEXT : undefined
              }
              {...register(field.name)}
            />
          </FormField>
        </FieldWrapper>
      );

    case 'scale': {
      const min = field.scaleMin ?? 1;
      const max = field.scaleMax ?? 10;
      const scaleValues = Array.from({ length: max - min + 1 }, (_, index) =>
        String(min + index),
      );

      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: rhfField }) => {
            const selectedValue = rhfField.value != null ? String(rhfField.value) : '';

            return (
              <FieldWrapper name={field.name} errorMessage={errorMessage}>
                <Typography bold color={colors.text.primary}>
                  {field.label}
                </Typography>
                <Row
                  width="100%"
                  justify="between"
                  align="center"
                  role="group"
                  aria-label={field.label}
                  data-cy={`bid-scale-${field.name}`}
                >
                  {scaleValues.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      color={selectedValue === value ? 'primary' : 'secondary'}
                      onClick={() => rhfField.onChange(Number(value))}
                    />
                  ))}
                </Row>
                <Row width="100%" justify="between">
                  <Typography variant="caption" color={colors.text.secondary}>
                    Poor
                  </Typography>
                  <Typography variant="caption" color={colors.text.secondary}>
                    Excellent
                  </Typography>
                </Row>
              </FieldWrapper>
            );
          }}
        />
      );
    }

    case 'radio':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: rhfField }) => (
            <FieldWrapper name={field.name} errorMessage={errorMessage}>
              <RadioGroup
                label={field.label}
                value={rhfField.value ?? ''}
                onChange={rhfField.onChange}
                error={Boolean(errorMessage)}
              >
                {(field.options ?? []).map((option) => (
                  <Radio key={option} value={option} label={option} />
                ))}
              </RadioGroup>
            </FieldWrapper>
          )}
        />
      );

    case 'boolean':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: rhfField }) => (
            <FieldWrapper name={field.name} errorMessage={errorMessage}>
              <Checkbox
                checked={Boolean(rhfField.value)}
                onChange={rhfField.onChange}
                label={renderBidTermsLabel(field.label)}
                error={Boolean(errorMessage)}
                // Multi-line label wraps; keep the checkbox icon top-aligned
                // instead of vertically centered against the wrapped text.
                className="items-start"
              />
            </FieldWrapper>
          )}
        />
      );

    default:
      return null;
  }
};
